import { getReq, postReq, putReq, deleteReq, createUrl } from '@/infra/http';
import { PagedResponseModel, BaseResponseModel, PaginationFilterRequest, PaginatedSelectModel } from '@/models/core.model';
import {
  EnergyHub, EnergyHubClubRunCandidate, EnergyHubClubVisit, EnergyHubClubVisitPaginationFilter, EnergyHubCheckInTag,
  EnergyHubCreditRequest, EnergyHubCreditTransaction, EnergyHubHostingSlot, EnergyHubList, EnergyHubOffer,
  EnergyHubPaginationFilter, EnergyHubReceipt, EnergyHubReceiptApproveRequest, EnergyHubReceiptPaginationFilter,
  EnergyHubVisit, EnergyHubVisitPaginationFilter, UpsertEnergyHub, UpsertEnergyHubClubVisit, UpsertEnergyHubOffer,
  energyHubClubRunCandidateFromJson, energyHubClubVisitFromJson, energyHubCreditTransactionFromJson, energyHubFromJson,
  energyHubListFromJson, energyHubOfferFromJson, energyHubReceiptFromJson, energyHubVisitFromJson,
} from '@/models/energy-hub.model';

const base = 'a/EnergyHub';
const receiptBase = 'a/EnergyHub/Receipt';

const paged = <T>(res: any, map: (json: any) => T): PagedResponseModel<T> => ({
  ...res.data,
  result: res.data.result.map(map),
});

// ---------- Hubs ----------

export const getPagedEnergyHubs = async (input: EnergyHubPaginationFilter): Promise<PagedResponseModel<EnergyHubList>> => {
  return await getReq(createUrl(`${base}/PagedData`), { params: input }).then((res: any) => paged(res, energyHubListFromJson));
};

export const getEnergyHubsLookup = async (input?: string, page?: number): Promise<PaginatedSelectModel> => {
  const model: EnergyHubPaginationFilter = {
    pageNumber: page ?? 1,
    pageSize: 100,
    keyword: input,
    orderBy: 'Name',
    isAscending: true,
  };
  return await getPagedEnergyHubs(model).then(data => ({
    data: data.result.map(itm => ({ value: itm.id, label: itm.name })),
    isLastPage: data.pageNumber >= data.totalPages,
  }));
};

export const getEnergyHub = async (id: number): Promise<BaseResponseModel<EnergyHub>> => {
  return await getReq(createUrl(`${base}/${id}`))
    .then((res: any) => ({ ...res.data, result: energyHubFromJson(res.data.result) }));
};

export const upsertEnergyHub = async (input: UpsertEnergyHub): Promise<BaseResponseModel<EnergyHub>> => {
  const { id, ...body } = input;
  const req = id ? putReq(createUrl(`${base}/${id}`), body) : postReq(createUrl(base), body);
  return await req.then((res: any) => res.data);
};

// ---------- Offers / slots / tags ----------

export const createEnergyHubOffer = async (id: number, input: UpsertEnergyHubOffer): Promise<BaseResponseModel<EnergyHubOffer>> => {
  return await postReq(createUrl(`${base}/${id}/Offer`), input)
    .then((res: any) => ({ ...res.data, result: energyHubOfferFromJson(res.data.result) }));
};

export const updateEnergyHubOffer = async (id: number, offerId: number, input: UpsertEnergyHubOffer): Promise<BaseResponseModel<EnergyHubOffer>> => {
  return await putReq(createUrl(`${base}/${id}/Offer/${offerId}`), input)
    .then((res: any) => ({ ...res.data, result: energyHubOfferFromJson(res.data.result) }));
};

export const deleteEnergyHubOffer = async (id: number, offerId: number): Promise<BaseResponseModel<null>> => {
  return await deleteReq(createUrl(`${base}/${id}/Offer/${offerId}`)).then((res: any) => res.data);
};

export const setEnergyHubHostingSlots = async (id: number, slots: EnergyHubHostingSlot[]): Promise<BaseResponseModel<EnergyHubHostingSlot[]>> => {
  return await putReq(createUrl(`${base}/${id}/HostingSlots`), slots).then((res: any) => res.data);
};

export const createEnergyHubTag = async (id: number, label?: string): Promise<BaseResponseModel<EnergyHubCheckInTag>> => {
  return await postReq(createUrl(`${base}/${id}/Tag`), { label }).then((res: any) => res.data);
};

export const setEnergyHubTagActive = async (id: number, tagId: number, isActive: boolean): Promise<BaseResponseModel<EnergyHubCheckInTag>> => {
  return await putReq(createUrl(`${base}/${id}/Tag/${tagId}/Active`), { isActive }).then((res: any) => res.data);
};

// ---------- Club visits (Hub Runs) ----------

export const upsertEnergyHubClubVisit = async (id: number, input: UpsertEnergyHubClubVisit): Promise<BaseResponseModel<EnergyHubClubVisit>> => {
  return await postReq(createUrl(`${base}/${id}/ClubVisit`), input).then((res: any) => res.data);
};

export const setEnergyHubClubVisitStatus = async (
  id: number, clubVisitId: number, input: UpsertEnergyHubClubVisit,
): Promise<BaseResponseModel<EnergyHubClubVisit>> => {
  return await putReq(createUrl(`${base}/${id}/ClubVisit/${clubVisitId}/Status`), { status: input.status, note: input.note })
    .then((res: any) => res.data);
};

/** Hub Runs across all hubs, e.g. status Requested for club requests to review. */
export const getPagedAllEnergyHubClubVisits = async (input: EnergyHubClubVisitPaginationFilter): Promise<PagedResponseModel<EnergyHubClubVisit>> => {
  return await getReq(createUrl(`${base}/ClubVisit/PagedData`), { params: input })
    .then((res: any) => paged(res, energyHubClubVisitFromJson));
};

export const getPagedEnergyHubClubVisits = async (id: number, input: EnergyHubClubVisitPaginationFilter): Promise<PagedResponseModel<EnergyHubClubVisit>> => {
  return await getReq(createUrl(`${base}/${id}/ClubVisit/PagedData`), { params: input })
    .then((res: any) => paged(res, energyHubClubVisitFromJson));
};

// ---------- Visit log ----------

export const getPagedEnergyHubVisits = async (id: number, input: EnergyHubVisitPaginationFilter): Promise<PagedResponseModel<EnergyHubVisit>> => {
  return await getReq(createUrl(`${base}/${id}/Visit/PagedData`), { params: input })
    .then((res: any) => paged(res, energyHubVisitFromJson));
};

// ---------- Credit ----------

export type EnergyHubCreditAction = 'TopUp' | 'Refund' | 'Adjust';

export const postEnergyHubCredit = async (
  id: number, action: EnergyHubCreditAction, input: EnergyHubCreditRequest,
): Promise<BaseResponseModel<EnergyHubCreditTransaction>> => {
  return await postReq(createUrl(`${base}/${id}/Credit/${action}`), input).then((res: any) => res.data);
};

export const getPagedEnergyHubCreditTransactions = async (
  id: number, input: PaginationFilterRequest,
): Promise<PagedResponseModel<EnergyHubCreditTransaction>> => {
  return await getReq(createUrl(`${base}/${id}/Credit/Transaction/PagedData`), { params: input })
    .then((res: any) => paged(res, energyHubCreditTransactionFromJson));
};

// ---------- Receipts ----------

export const getPagedEnergyHubReceipts = async (input: EnergyHubReceiptPaginationFilter): Promise<PagedResponseModel<EnergyHubReceipt>> => {
  return await getReq(createUrl(`${receiptBase}/PagedData`), { params: input })
    .then((res: any) => paged(res, energyHubReceiptFromJson));
};

export const getEnergyHubReceipt = async (id: number): Promise<BaseResponseModel<EnergyHubReceipt>> => {
  return await getReq(createUrl(`${receiptBase}/${id}`))
    .then((res: any) => ({ ...res.data, result: energyHubReceiptFromJson(res.data.result) }));
};

export const getEnergyHubReceiptCandidates = async (id: number, receiptTime?: Date): Promise<BaseResponseModel<EnergyHubClubRunCandidate[]>> => {
  return await getReq(createUrl(`${receiptBase}/${id}/ClubRunCandidates`), { params: { receiptTime: receiptTime?.toISOString() } })
    .then((res: any) => ({ ...res.data, result: (res.data.result ?? []).map(energyHubClubRunCandidateFromJson) }));
};

export const approveEnergyHubReceipt = async (id: number, input: EnergyHubReceiptApproveRequest): Promise<BaseResponseModel<EnergyHubReceipt>> => {
  return await postReq(createUrl(`${receiptBase}/${id}/Approve`), input).then((res: any) => res.data);
};

export const rejectEnergyHubReceipt = async (id: number, reason: string): Promise<BaseResponseModel<EnergyHubReceipt>> => {
  return await postReq(createUrl(`${receiptBase}/${id}/Reject`), { reason }).then((res: any) => res.data);
};
